import { FC, useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { Dialog, DialogTrigger } from '@/components/ui/dialog'
import useObject from '@/hooks/useObject'
import TechnicalDetailsDialog from '@/pages/settings/customer-lookup/TechnicalDetailsDialog'
import { retrieveCustomerLookup } from '@/thunks/customer-lookup/retrieve'
import fadeIn from '@/util/fadeIn'

import FormCard from './FormCard'
import TestSheet from './TestSheet'
import useCustomerLookupUpsert from './useCustomerLookupUpsert'
import { Settings } from '@/components/layout/settings/layout'
import SettingsTitle from '@/components/layout/settings/title'

const FormSchema = z.object({
  url: z
    .string()
    .url({
      message: 'URL must be a valid URL.',
    })
    .startsWith('https://', {
      message: 'URL must start with https://.',
    }),
  method: z.enum(['GET', 'POST']),
  headers: z
    .array(
      z
        .object({
          key: z.string().min(1, { message: 'Header name is required' }),
          value: z.string().min(1, { message: 'Value is required' }),
        })
        .required()
    )
    .max(3, { message: 'You can add up to 3 headers' }),
  customerPath: z.string().min(1, { message: 'Customers array path is required' }),
  customerId: z.string().min(1, { message: 'Customer ID path is required' }),
  titleTemplate: z.string().min(1, { message: 'Title template is required' }),
  descriptionTemplate: z.string().min(1, { message: 'Description template is required' }),
})

export type FormData = z.infer<typeof FormSchema>

const CustomerLookup: FC = () => {
  const [lookup, { wasLoaded, set }] = useObject(retrieveCustomerLookup, {})
  const [upsert, { timer }] = useCustomerLookupUpsert()

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
  })

  useEffect(() => {
    if (!lookup) return
    form.reset({
      ...lookup,
      method: lookup.method || 'GET',
      headers: lookup.headerKeys.map(header => ({ key: header, value: '' })),
    })
  }, [lookup, form])

  const handleSubmit = useCallback(
    async (data: FormData) => {
      try {
        const resp = await upsert(data)
        set(resp)
      } catch {
        toast.error('Failed to save customer lookup settings')
      }
    },
    [set, upsert]
  )

  const handleRemove = useCallback(async () => {
    try {
      await upsert({
        url: '',
        method: 'GET',
        headers: [],
        customerPath: '',
        customerId: '',
        titleTemplate: '',
        descriptionTemplate: '',
      })

      set(undefined)
      form.reset({
        url: '',
        method: 'GET',
        headers: [],
        customerPath: '',
        customerId: '',
        titleTemplate: '',
        descriptionTemplate: '',
      })
    } catch {
      toast.error('Failed to remove customer lookup settings')
    }
  }, [form, upsert, set])

  return (
    <Settings>
      <Dialog>
        <FormProvider {...form}>
          <div className="space-y-6">
            {/* Header Section */}
            <div>
              <SettingsTitle
                title="Customer Lookup"
                subtitle="Configure your API endpoint to enable customer lookup functionality."
              >
                <DialogTrigger>
                  <Button variant="outline">View Technical Details</Button>
                </DialogTrigger>
              </SettingsTitle>
            </div>

            <div className={fadeIn()}>
              {wasLoaded && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                  <div className="lg:col-span-3">
                    <form onSubmit={form.handleSubmit(handleSubmit)}>
                      <FormCard timer={timer} onRemove={handleRemove} />
                    </form>
                  </div>

                  <div className="lg:col-span-2">
                    <TestSheet />
                  </div>
                </div>
              )}
              <TechnicalDetailsDialog />
            </div>
          </div>
        </FormProvider>
      </Dialog>
    </Settings>
  )
}

export default CustomerLookup
